import * as data from "../data";
import * as actionTypes from "../constants";

const initialState = {
  loading: false,
  error: null,
  dashboardData: {},
  reportOptions: data.reportOptions(),
  categories: data.categories(),
  memories: {
    longTerm: data.longTermMemory(),
    shortTerm: data.shortTermMemory(),
  },
  playReport: {},
  moduleReport: {},
  cognitiveFitness: null,
  cognitiveFitnessLoading: false,
  sessionSummary: null,
  sessionSummaryLoading: false,
  behaviouralReport: [],
  behaviouralReportLoading: false,
  exerciseActivity: null,
  exerciseActivityLoading: false,
  meditationActivity: null,
  meditationActivityLoading: false,
  playReportById: null,
  playReportByIdLoading: false,
  modulePlayReportById: null,
  modulePlayReportByIdLoading: false,
  activitySummary: null,
  activitySummaryLoading: false,
};

export default function dashboard(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case actionTypes.FETCH_REPORTS:
    case actionTypes.FETCH_PLAY_REPORTS:
      return {
        ...state,
        loading: true,
      };

    case actionTypes.GET_REPORT_SUCCESS: {
      // Find Memory category by name (not hardcoded index)
      const memoryEntry =
        action?.payload?.length > 0
          ? action.payload[0]?.DashboardScores?.find(
              (d) => d.name?.toLowerCase() === "memory" || d.Name?.toLowerCase() === "memory"
            )
          : null;
      const memSubList = memoryEntry?.subCategoryList || memoryEntry?.SubCategoryList || [];
      const longTerm = memSubList.find((s) => (s.name || s.Name || "").toLowerCase().includes("long")) || memSubList[0] || null;
      const shortTerm = memSubList.find((s) => (s.name || s.Name || "").toLowerCase().includes("short")) || memSubList[1] || null;
      return {
        ...state,
        loading: false,
        dashboardData: action.payload,
        memories: {
          longTerm,
          shortTerm,
        },
        error: null,
      };
    }

    case actionTypes.GET_PLAY_REPORT_SUCCESS:
      return {
        ...state,
        loading: false,
        playReport: action.payload,
      };

    case actionTypes.GET_MODULE_REPORT_SUCCESS:
      return {
        ...state,
        loading: false,
        moduleReport: action.payload,
      };

    case actionTypes.GET_REPORT_CLEAR:
      return {
        ...initialState,
      };

    case actionTypes.FETCH_COGNITIVE_FITNESS:
      return { ...state, cognitiveFitnessLoading: true };
    case actionTypes.GET_COGNITIVE_FITNESS_SUCCESS:
      return { ...state, cognitiveFitnessLoading: false, cognitiveFitness: payload, error: null };
    case actionTypes.GET_COGNITIVE_FITNESS_FAIL:
      return { ...state, cognitiveFitnessLoading: false, error: payload };

    case actionTypes.FETCH_SESSION_SUMMARY:
      return { ...state, sessionSummaryLoading: true };
    case actionTypes.GET_SESSION_SUMMARY_SUCCESS:
      return { ...state, sessionSummaryLoading: false, sessionSummary: payload, error: null };
    case actionTypes.GET_SESSION_SUMMARY_FAIL:
      return { ...state, sessionSummaryLoading: false, error: payload };

    case actionTypes.FETCH_BEHAVIOURAL_REPORT:
      return { ...state, behaviouralReportLoading: true };
    case actionTypes.GET_BEHAVIOURAL_REPORT_SUCCESS:
      return { ...state, behaviouralReportLoading: false, behaviouralReport: payload || [], error: null };
    case actionTypes.GET_BEHAVIOURAL_REPORT_FAIL:
      return { ...state, behaviouralReportLoading: false, error: payload };

    case actionTypes.FETCH_EXERCISE_ACTIVITY:
      return { ...state, exerciseActivityLoading: true };
    case actionTypes.GET_EXERCISE_ACTIVITY_SUCCESS:
      return { ...state, exerciseActivityLoading: false, exerciseActivity: payload, error: null };
    case actionTypes.GET_EXERCISE_ACTIVITY_FAIL:
      return { ...state, exerciseActivityLoading: false, error: payload };

    case actionTypes.FETCH_MEDITATION_ACTIVITY:
      return { ...state, meditationActivityLoading: true };
    case actionTypes.GET_MEDITATION_ACTIVITY_SUCCESS:
      return { ...state, meditationActivityLoading: false, meditationActivity: payload, error: null };
    case actionTypes.GET_MEDITATION_ACTIVITY_FAIL:
      return { ...state, meditationActivityLoading: false, error: payload };

    case actionTypes.FETCH_PLAY_REPORT_BY_ID:
      return { ...state, playReportByIdLoading: true };
    case actionTypes.GET_PLAY_REPORT_BY_ID_SUCCESS:
      return { ...state, playReportByIdLoading: false, playReportById: payload, error: null };
    case actionTypes.GET_PLAY_REPORT_BY_ID_FAIL:
      return { ...state, playReportByIdLoading: false, error: payload };

    case actionTypes.FETCH_MODULE_PLAY_REPORT_BY_ID:
      return { ...state, modulePlayReportByIdLoading: true };
    case actionTypes.GET_MODULE_PLAY_REPORT_BY_ID_SUCCESS:
      return { ...state, modulePlayReportByIdLoading: false, modulePlayReportById: payload, error: null };
    case actionTypes.GET_MODULE_PLAY_REPORT_BY_ID_FAIL:
      return { ...state, modulePlayReportByIdLoading: false, error: payload };

    case actionTypes.FETCH_ACTIVITY_SUMMARY:
      return { ...state, activitySummaryLoading: true };
    case actionTypes.GET_ACTIVITY_SUMMARY_SUCCESS: {
      const summary = payload || {};

      // Parse dashboardScores JSON string (kept for chart/subcategory fallback only)
      let parsedDashboard = null;
      if (summary.dashboardScores) {
        try { parsedDashboard = JSON.parse(summary.dashboardScores); } catch {}
      }

      // Build memories from the new categories array (SubCategories from BuildCategoryScoresAsync)
      const cats = summary.categories || [];
      const normSub = (item) => ({
        name:  item.Name  || item.name  || "",
        score: item.Score ?? item.score ?? 0,
        Chart: (item.Chart || item.chart || []).map((p) => ({
          date: p.Date || p.date || "",
          score: p.Score ?? p.score ?? 0,
        })),
        SubSubCategoryList: [],
      });

      const ltmCat  = cats.find((c) => (c.Category || c.category || "").toLowerCase().includes("long-term memory")  || (c.Category || c.category || "").toLowerCase().includes("long term memory"));
      const stmCat  = cats.find((c) => (c.Category || c.category || "").toLowerCase().includes("short-term memory") || (c.Category || c.category || "").toLowerCase().includes("short term memory"));

      // Sub-categories of LTM (Episodic, Procedural, Semantic) → SubSubCategoryList for the donut drill-down
      const ltmSubs = (ltmCat?.SubCategories || ltmCat?.subCategories || []).map(normSub);
      const stmSubs = (stmCat?.SubCategories || stmCat?.subCategories || []).map(normSub);

      // Build longTerm / shortTerm objects with Score + Chart + SubSubCategoryList
      const longTerm = ltmCat
        ? { Score: ltmCat.Score ?? ltmCat.score ?? 0, Chart: ltmSubs[0]?.Chart || [], SubSubCategoryList: ltmSubs, Trend: null }
        : state.memories.longTerm;
      const shortTerm = stmCat
        ? { Score: stmCat.Score ?? stmCat.score ?? 0, Chart: stmSubs[0]?.Chart || [], SubSubCategoryList: stmSubs, Trend: null }
        : state.memories.shortTerm;

      return {
        ...state,
        activitySummaryLoading: false,
        activitySummary: summary,
        cognitiveFitness: summary.cognitiveFitness != null
          ? { fitnessScore: summary.cognitiveFitness }
          : state.cognitiveFitness,
        sessionSummary:        summary.sessionSummary    || state.sessionSummary,
        exerciseActivity:      summary.exercise          || state.exerciseActivity,
        meditationActivity:    summary.meditation        || state.meditationActivity,
        behaviouralReport:     summary.behavioural       || state.behaviouralReport,
        modulePlayReportById:  summary.moduleReport      || state.modulePlayReportById,
        dashboardData: parsedDashboard || state.dashboardData,
        memories: { longTerm, shortTerm },
        error: null,
      };
    }
    case actionTypes.GET_ACTIVITY_SUMMARY_FAIL:
      return { ...state, activitySummaryLoading: false, error: payload };

    case actionTypes.GET_REPORT_FAIL:
    case actionTypes.GET_PLAY_REPORT_FAIL:
    case actionTypes.GET_MODULE_REPORT_FAIL:
      return {
        ...state,
        loading: false,
        error: payload,
      };

    default:
      return state;
  }
}
